// src/stores/useWebSocketStore.ts
import { defineStore } from 'pinia'
import { io, Socket } from 'socket.io-client'
import { ref, computed, watch } from 'vue'
import oidcAuth from '@/auth/oidcAuth'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import type { MessageType } from '@/utils/types'
import { createSecureAuthPayload } from '@/utils/tokenUtils'

export const useWebSocketStore = defineStore('websocket', () => {
  interface WebSocketStructuredMessage {
    type: MessageType
    message: string
    details?: Record<string, any>
  }

  interface Notification {
    message: string
    type: MessageType
    details: any
  }
  interface MessageAuth {
    message: string
    status: string
  }

  const socket = ref<Socket | null>(null)
  const isConnected = ref(false)
  const isAuthenticated = ref(false)
  const notification = ref<Notification | null>(null)
  const progressInformations = ref<WebSocketStructuredMessage | null>(null)
  const cegidJobProgress = ref<WebSocketStructuredMessage | null>(null)
  let lastAuthenticatedEmail: string | null = null

  // Source de vérité unique : l'identité boost-core (peuplée après coreLogin / coreLoginPassword + fetchMe).
  // Pas de fallback OIDC : on attend que la session boost-core soit établie avant d'ouvrir la WS.
  const userEmail = computed(() => useBoostCorePermissionStore().user?.email)

  function connect() {
    // Idempotent : si une socket est déjà ouverte ou en cours de connexion, on ne refait rien.
    // Permet d'appeler connect() depuis tous les sites où userConnected est posé sans race.
    if (socket.value && (socket.value.connected || socket.value.active)) return
    if (socket.value) {
      socket.value.disconnect()
    }

    const apiUrl = import.meta.env.VITE_WS_URL

    socket.value = io(apiUrl, {
      withCredentials: true,
      transports: ['websocket'],
      secure: true,
      extraHeaders: {
        Origin: window.location.origin,
      },
    })

    socket.value.on('connect', () => {
      isConnected.value = true
      console.debug('Connecté au serveur WebSocket')
      tryAuthenticate()
    })

    socket.value.on('disconnect', () => {
      isConnected.value = false
      isAuthenticated.value = false
      lastAuthenticatedEmail = null
      console.debug('Déconnecté du serveur WebSocket')
    })

    socket.value.on('notification', (data) => {
      notification.value = data as Notification
    })

    socket.value.on('campaign-progress', (data) => {
      progressInformations.value = data
    })

    socket.value.on('cegid-job-progress', (data) => {
      cegidJobProgress.value = data as WebSocketStructuredMessage
    })
  }

  function tryAuthenticate() {
    if (!socket.value || !isConnected.value || !userEmail.value) return
    if (lastAuthenticatedEmail === userEmail.value) return
    authenticate()
  }

  function authenticate() {
    if (!socket.value || !isConnected.value || !userEmail.value) {
      console.error("Impossible de s'authentifier: non connecté ou email manquant")
      return false
    }

    const targetEmail = userEmail.value

    socket.value.emit(
      'authenticate',
      createSecureAuthPayload(targetEmail, oidcAuth.accessToken),
      (response: MessageAuth) => {
        if (response && response.status === 'success') {
          isAuthenticated.value = true
          lastAuthenticatedEmail = targetEmail
          console.debug('Authentifié avec succès sur WebSocket', targetEmail)
        } else {
          isAuthenticated.value = false
          lastAuthenticatedEmail = null
          console.error("Échec de l'authentification WebSocket:", response?.message)
        }
      },
    )
  }

  function disconnect() {
    if (socket.value) {
      socket.value.disconnect()
      socket.value = null
    }
    isConnected.value = false
    isAuthenticated.value = false
  }

  function sendMessage(event: string, payload: any) {
    if (!socket.value || !isConnected.value) {
      console.error("Impossible d'envoyer un message: non connecté")
      return false
    }

    socket.value.emit(event, payload)
    return true
  }

  // Lifecycle WS piloté par l'identité boost-core (login email/oidc → fetchMe → user, logout → user=null).
  // Un seul point d'entrée : pas besoin d'appeler connect() depuis main.ts/LoginPage/router/oidcAuth.
  // immediate: true → si le store est instancié alors que user est déjà set (reload, mount tardif), connect tout de suite.
  watch(userEmail, (email) => {
    if (email) connect()
    else disconnect()
  }, { immediate: true })

  // Auth (et re-auth pour trickster) dès que socket connectée ET identité dispo.
  // Idempotent grâce à lastAuthenticatedEmail.
  watch([userEmail, isConnected], () => tryAuthenticate())

  return {
    socket,
    isConnected,
    isAuthenticated,
    notification,
    progressInformations,
    cegidJobProgress,
    authenticate,
    disconnect,
    connect,
    sendMessage,
  }
})
